import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Save, X, Calendar, Briefcase, MapPin, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage({ user, onUpdateUser }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);

  // Edit profile state
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Change password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Fetch user profile
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProfile(response.data.user);
        setUsername(response.data.user.username || '');
        setFullName(response.data.user.full_name || '');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Gagal memuat profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!username.trim()) {
      toast.error('Username tidak boleh kosong');
      return;
    }
    
    if (!fullName.trim()) {
      toast.error('Nama lengkap tidak boleh kosong');
      return;
    }

    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        { username: username, full_name: fullName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setProfile(response.data.user);
        setEditMode(false);
        toast.success('Profile berhasil diupdate');
        
        // Update user context if callback provided
        if (onUpdateUser) {
          onUpdateUser(response.data.user);
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Gagal update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('Semua field harus diisi');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Password baru tidak cocok');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/auth/profile/password`,
        {
          old_password: oldPassword,
          new_password: newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Password berhasil diubah. Anda akan logout otomatis...');
        
        // Auto logout after password change (security best practice)
        setTimeout(() => {
          // Clear all auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Redirect to login
          navigate('/login');
        }, 2000); // 2 second delay for user to see the message
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Gagal ubah password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-600 text-white';
      case 'supervisor':
        return 'bg-blue-600 text-white';
      case 'kasir':
        return 'bg-green-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen billing-page-shell flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen billing-page-shell flex items-center justify-center">
        <div className="text-white text-lg">Profile tidak ditemukan</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen billing-page-shell p-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header with Back Button */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Profile Saya</h1>
            <p className="text-blue-200">Kelola informasi akun dan keamanan Anda</p>
          </div>
          <button
            onClick={() => {
              const userRole = profile.role;
              if (userRole === 'kasir' || userRole === 'supervisor') {
                navigate('/kasir');
              } else if (userRole === 'admin') {
                navigate('/admin');
              } else {
                navigate(-1);
              }
            }}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali ke Dashboard
          </button>
        </div>

        {/* Profile Card */}
        <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-lg rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{profile.full_name || profile.username}</h2>
                <p className="text-blue-100">@{profile.username}</p>
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="p-6 space-y-6">
            {/* Edit Profile Section */}
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-400" />
                  Informasi Profile
                </h3>
                {!editMode && !passwordMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
                  >
                    Edit Profile
                  </button>
                )}
              </div>

              {editMode ? (
                // Edit Mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Masukkan username"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nama Lengkap
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleUpdateProfile}
                      disabled={savingProfile}
                      className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {savingProfile ? 'Menyimpan...' : 'Simpan'}
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setUsername(profile.username || '');
                        setFullName(profile.full_name || '');
                      }}
                      className="px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Batal
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Username</label>
                    <p className="text-white font-medium">{profile.username}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nama Lengkap</label>
                    <p className="text-white font-medium">{profile.full_name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      Role
                    </label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getRoleBadgeColor(profile.role)}`}>
                      {profile.role.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Cabang
                    </label>
                    <p className="text-white font-medium">{profile.branch || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Bergabung Sejak
                    </label>
                    <p className="text-white font-medium">{formatDate(profile.created_at)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Change Password Section */}
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-yellow-400" />
                  Keamanan Akun
                </h3>
                {!passwordMode && !editMode && (
                  <button
                    onClick={() => setPasswordMode(true)}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition"
                  >
                    Ubah Password
                  </button>
                )}
              </div>

              {passwordMode ? (
                // Password Change Form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Password Lama
                    </label>
                    <input
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Masukkan password lama"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Password Baru
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Minimal 6 karakter"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Konfirmasi Password Baru
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Ketik ulang password baru"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {changingPassword ? 'Mengubah...' : 'Ubah Password'}
                    </button>
                    <button
                      onClick={() => {
                        setPasswordMode(false);
                        setOldPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Batal
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <p className="text-gray-400">
                  Password Anda aman. Klik tombol &quot;Ubah Password&quot; untuk mengubah password akun Anda.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
